/**
 * 멀티 문서 머지 유틸리티
 * 페이지별 추출 결과를 매칭 키 기준으로 그룹핑하고 병합
 * 
 * 지원하는 관계: 1:1, 1:N, N:1, N:N
 */

/**
 * 페이지 결과를 매칭 키 기준으로 그룹핑하고 병합
 * @param {Array} pageResults - 페이지별 추출 결과 [{pageNumber, extractedData, documentType, ...}]
 * @param {Object} templates - 템플릿 정보 객체
 * @param {Object} options - 옵션 {defaultMatchKey, enableMerge}
 * @returns {Array} 머지된 그룹 배열
 */
export function mergePageResults(pageResults, templates, options = {}) {
    const {
        defaultMatchKey = '성명',  // 기본 매칭 키
        enableMerge = true         // 머지 활성화 여부
    } = options;

    if (!enableMerge || !pageResults || pageResults.length === 0) {
        // 머지 비활성화 시 각 페이지를 개별 그룹으로
        return pageResults.map(page => ({
            id: `page_${page.pageNumber}`,
            type: 'single',
            matchKey: null,
            matchValue: null,
            sourcePages: [{
                pageNumber: page.pageNumber,
                docType: page.documentType || page.extractedData?.documentType || '미분류',
                priority: 99,
                data: page.extractedData || page.flashData || {}
            }],
            mergedData: page.extractedData || page.flashData || {},
            conflicts: []
        }));
    }

    const groups = new Map();
    const templatesArray = Object.values(templates || {});

    // Step 1: 모든 페이지를 매칭 키로 그룹핑
    for (const page of pageResults) {
        const pageData = page.extractedData || page.flashData || page.proData || {};
        const docType = pageData.documentType || page.documentType || '미분류';

        // 템플릿 찾기
        const template = findTemplateByDocType(templatesArray, docType);

        // 매칭 키 결정 (템플릿 설정 > primaryKey > 기본값)
        const matchKey = template?.mergeConfig?.matchKey
            || template?.primaryKey
            || defaultMatchKey;

        // 매칭 값 추출
        const matchValue = extractMatchValue(pageData, matchKey, template?.mergeConfig?.matchKeyAliases);

        if (!matchValue) {
            // 매칭 값 없으면 개별 그룹
            const individualId = `page_${page.pageNumber}_${docType}`;
            groups.set(individualId, {
                id: individualId,
                type: 'single',
                matchKey: null,
                matchValue: null,
                sourcePages: [{
                    pageNumber: page.pageNumber,
                    docType,
                    priority: template?.mergeConfig?.priority || 99,
                    data: pageData
                }],
                mergedData: { ...pageData },
                conflicts: []
            });
            continue;
        }

        // 그룹 ID 생성 (매칭키_매칭값)
        const groupId = `${matchKey}_${matchValue}`;

        if (!groups.has(groupId)) {
            groups.set(groupId, {
                id: groupId,
                type: 'merged',
                matchKey,
                matchValue,
                sourcePages: [],
                mergedData: {},
                conflicts: []
            });
        }

        const group = groups.get(groupId);
        group.sourcePages.push({
            pageNumber: page.pageNumber,
            docType,
            priority: template?.mergeConfig?.priority || 99,
            data: pageData
        });
    }

    // Step 2: 각 그룹 내에서 우선순위 정렬 후 머지
    for (const group of groups.values()) {
        if (group.type === 'single') continue;

        // 우선순위 정렬 (낮은 숫자가 높은 우선순위)
        group.sourcePages.sort((a, b) => a.priority - b.priority);

        // 순차적으로 머지
        for (const source of group.sourcePages) {
            mergeWithPriority(group.mergedData, source.data, group.conflicts);
        }

        // 단일 페이지면 type을 single로 변경
        if (group.sourcePages.length === 1) {
            group.type = 'single';
        }
    }

    // 결과 배열로 변환 (페이지 순서 유지)
    const result = Array.from(groups.values());

    // 첫 번째 페이지 기준으로 정렬
    result.sort((a, b) => {
        const aFirstPage = Math.min(...a.sourcePages.map(p => p.pageNumber));
        const bFirstPage = Math.min(...b.sourcePages.map(p => p.pageNumber));
        return aFirstPage - bFirstPage;
    });

    console.log(`📦 [MergeUtils] ${pageResults.length}페이지 → ${result.length}그룹 (머지됨: ${result.filter(g => g.type === 'merged').length})`);

    return result;
}

/**
 * 문서 타입으로 템플릿 찾기
 */
function findTemplateByDocType(templates, docType) {
    if (!templates || !docType) return null;

    return templates.find(t =>
        t.name === docType ||
        t.documentType === docType ||
        t.name?.toLowerCase() === docType?.toLowerCase()
    );
}

/**
 * 매칭 값 추출 (별칭 지원 + 공백 무시)
 */
function extractMatchValue(data, key, aliases = []) {
    if (!data || !key) return null;

    // 🔧 FIX: Normalize by removing spaces
    const normalizedKey = key.replace(/\s/g, '').toLowerCase();

    // 직접 키 체크
    if (data[key] && data[key].trim()) return data[key].trim();

    // 별칭 체크
    const allKeys = [key, ...(aliases || [])];
    for (const alias of allKeys) {
        if (data[alias] && data[alias].trim()) {
            return data[alias].trim();
        }
    }

    // 정규화된 키 찾기 (공백 제거 후 비교)
    const dataKeys = Object.keys(data);
    for (const dataKey of dataKeys) {
        const normalizedDataKey = dataKey.replace(/\s/g, '').toLowerCase();

        if (normalizedDataKey === normalizedKey ||
            normalizedDataKey.includes(normalizedKey) ||
            normalizedKey.includes(normalizedDataKey)) {
            if (data[dataKey] && String(data[dataKey]).trim()) {
                return String(data[dataKey]).trim();
            }
        }
    }

    return null;
}

/**
 * 우선순위 기반 머지 (빈 필드만 채우거나 충돌 기록)
 */
function mergeWithPriority(target, source, conflicts) {
    if (!source) return;

    for (const [key, value] of Object.entries(source)) {
        // documentType, metadata 등 시스템 필드는 건너뜀
        if (key === 'documentType' || key === 'metadata') continue;

        // 빈 값은 무시
        if (value === null || value === undefined) continue;

        // 🔧 배열은 그대로 보존 (ItemList 등 테이블 데이터)
        if (Array.isArray(value)) {
            if (value.length === 0) continue;

            const existingValue = target[key];
            if (Array.isArray(existingValue) && existingValue.length > 0) {
                // 기존 배열에 새 항목 추가
                target[key] = [...existingValue, ...value];
            } else {
                target[key] = value;
            }
            continue;
        }

        // 문자열 변환
        const stringValue = String(value).trim();
        if (!stringValue || stringValue === 'null' || stringValue === 'undefined') continue;

        const existingValue = target[key];

        if (!existingValue || existingValue === '' || existingValue === null) {
            // 비어있으면 채움
            target[key] = stringValue;
        } else if (String(existingValue).trim() !== stringValue) {
            // 값이 다르면 충돌 기록 (이미 기록된 충돌인지 확인)
            const existingConflict = conflicts.find(c => c.field === key);
            if (!existingConflict) {
                conflicts.push({
                    field: key,
                    existing: String(existingValue).trim(),
                    new: stringValue,
                    resolved: String(existingValue).trim() // 우선순위 높은 값 유지
                });
            }
        }
        // 같은 값이면 무시
    }
}

/**
 * 머지 그룹에서 특정 문서 타입의 페이지 찾기
 */
export function findPagesByDocType(group, docType) {
    if (!group || !group.sourcePages) return [];
    return group.sourcePages.filter(p =>
        p.docType === docType ||
        p.docType?.toLowerCase() === docType?.toLowerCase()
    );
}

/**
 * 그룹에 포함된 문서 타입 목록
 */
export function getDocTypesInGroup(group) {
    if (!group || !group.sourcePages) return [];
    const types = new Set(group.sourcePages.map(p => p.docType));
    return Array.from(types);
}

/**
 * 그룹의 첫 번째 페이지 번호
 */
export function getFirstPageNumber(group) {
    if (!group || !group.sourcePages || group.sourcePages.length === 0) return 1;
    return Math.min(...group.sourcePages.map(p => p.pageNumber));
}

/**
 * 그룹의 페이지 번호 목록 (정렬됨)
 */
export function getPageNumbers(group) {
    if (!group || !group.sourcePages) return [];
    return group.sourcePages.map(p => p.pageNumber).sort((a, b) => a - b);
}

/**
 * 크로스 문서 필드 매핑 규칙 적용
 * 
 * @param {Array} pageResults - 페이지별 추출 결과
 * @param {Object} mergeRules - 머지 규칙 배열
 * @returns {Array} 규칙 적용된 페이지 결과
 * 
 * mergeRules 스키마 예시:
 * {
 *   sourceTemplate: "신분증",      // 원본 문서
 *   targetTemplate: "자동차등록말소증", // 대상 문서
 *   matchKey: "성명",              // 매칭 기준
 *   fieldMappings: [
 *     { sourceField: "주민등록번호", targetField: "생년월일" }
 *   ]
 * }
 */
export function applyMergeRules(pageResults, mergeRules) {
    if (!pageResults || pageResults.length === 0 || !mergeRules || mergeRules.length === 0) {
        return pageResults;
    }

    console.log(`🔄 [applyMergeRules] ${mergeRules.length}개 규칙 적용 중...`);

    // 🔧 Track which pages have been merged as sub documents
    const mergedSubPageNumbers = new Set();

    // 각 규칙 적용
    for (const rule of mergeRules) {
        const { mainIdentifier, subIdentifier, subExclude, matchKey, fieldMappings } = rule;

        // 필수 필드 검증 - fieldMappings는 빈 배열도 허용
        if (!mainIdentifier || !subIdentifier || !matchKey) {
            console.warn('⚠️ [applyMergeRules] 불완전한 규칙:', rule);
            continue;
        }

        // 쉼표 구분 필드 파싱
        const mainFields = mainIdentifier.split(',').map(f => f.trim()).filter(Boolean);
        const subFields = subIdentifier.split(',').map(f => f.trim()).filter(Boolean);
        const excludeFields = subExclude ? subExclude.split(',').map(f => f.trim()).filter(Boolean) : [];

        console.log(`📋 [applyMergeRules] 메인: [${mainFields.join(',')}] 서브: [${subFields.join(',')}] 매칭키: ${matchKey}`);

        // 필드 존재 여부로 메인/서브 분류
        const mainPages = [];
        const subPages = [];

        for (const page of pageResults) {
            const data = page.extractedData || {};

            // 메인 식별: mainFields 중 하나라도 값이 있으면 메인
            const isMain = mainFields.some(field => hasFieldValue(data, field));

            // 서브 식별: subFields 중 하나라도 있고, excludeFields는 없어야 함
            const hasSubField = subFields.some(field => hasFieldValue(data, field));
            const hasExcludeField = excludeFields.length > 0 && excludeFields.some(field => hasFieldValue(data, field));
            const isSub = hasSubField && !hasExcludeField && !isMain;

            if (isMain) {
                mainPages.push(page);
            } else if (isSub) {
                subPages.push(page);
            }
        }

        console.log(`  📊 분류 결과 - 메인: ${mainPages.length}개, 서브: ${subPages.length}개`);

        if (mainPages.length === 0 || subPages.length === 0) {
            console.log(`  ℹ️ 규칙 스킵 - 메인 또는 서브 페이지 없음`);
            continue;
        }

        // 서브 문서를 matchKey 값으로 인덱싱
        const subByMatchKey = new Map();
        for (const subPage of subPages) {
            const data = subPage.extractedData || {};
            const matchValue = extractMatchValueFromData(data, matchKey);
            if (matchValue) {
                // 🔧 Normalize match value for comparison
                const normalizedMatchValue = matchValue.replace(/\s/g, '').toLowerCase();
                subByMatchKey.set(normalizedMatchValue, { page: subPage, data, originalMatchValue: matchValue });
            }
        }

        // 메인 문서에 서브 문서 데이터 병합
        for (const mainPage of mainPages) {
            const mainData = mainPage.extractedData || {};
            const matchValue = extractMatchValueFromData(mainData, matchKey);

            if (!matchValue) continue;

            // 🔧 Normalize match value for comparison
            const normalizedMatchValue = matchValue.replace(/\s/g, '').toLowerCase();

            if (!subByMatchKey.has(normalizedMatchValue)) {
                continue;
            }

            const sub = subByMatchKey.get(normalizedMatchValue);

            console.log(`  🔗 병합: 메인 P${mainPage.pageNumber} ← 서브 P${sub.page.pageNumber} (매칭키: ${matchValue})`);

            // 🔧 모든 서브 필드를 메인에 복사 (fieldMappings가 비어있어도)
            if (!fieldMappings || fieldMappings.length === 0) {
                // fieldMappings가 없으면 모든 서브 필드를 복사 (빈 필드만)
                for (const [key, value] of Object.entries(sub.data)) {
                    if (key === 'documentType' || key === 'metadata') continue;
                    if (!value || String(value).trim() === '') continue;

                    if (!mainData[key] || mainData[key] === '') {
                        mainData[key] = value;
                        console.log(`    ✅ ${key}: ${value}`);
                    }
                }
            } else {
                // 필드 매핑 적용
                for (const mapping of fieldMappings) {
                    const { sourceField, targetField, mode = 'fillEmpty' } = mapping;
                    const sourceValue = sub.data[sourceField];

                    if (!sourceValue) continue;

                    const existingValue = mainData[targetField];

                    if (mode === 'fillEmpty' && (!existingValue || existingValue === '')) {
                        mainData[targetField] = sourceValue;
                        console.log(`    ✅ ${sourceField} → ${targetField}: ${sourceValue}`);
                    } else if (mode === 'overlay') {
                        mainData[targetField] = sourceValue;
                        console.log(`    🔄 ${sourceField} → ${targetField}: ${sourceValue} (덮어쓰기)`);
                    }
                }
            }

            // 업데이트된 데이터 반영
            mainPage.extractedData = mainData;

            // 🔧 Mark sub page as merged (to be filtered out later)
            mergedSubPageNumbers.add(sub.page.pageNumber);
            sub.page._mergedIntoMain = mainPage.pageNumber;
        }
    }

    // 🔧 Filter out merged sub documents from results
    const filteredResults = pageResults.filter(page => !mergedSubPageNumbers.has(page.pageNumber));

    console.log(`📦 [applyMergeRules] 결과: ${pageResults.length}페이지 → ${filteredResults.length}페이지 (${mergedSubPageNumbers.size}개 서브 문서 병합됨)`);

    return filteredResults;
}

/**
 * 필드에 유효한 값이 있는지 확인 (부분 매칭 지원 + 공백 무시)
 */
function hasFieldValue(data, fieldName) {
    if (!data || !fieldName) return false;

    // 🔧 FIX: Normalize by removing spaces for comparison
    const normalizedFieldName = fieldName.replace(/\s/g, '').toLowerCase();

    // 직접 매칭
    if (data[fieldName] && String(data[fieldName]).trim() !== '') {
        return true;
    }

    // 정규화된 매칭 (공백 제거 후 비교)
    for (const key of Object.keys(data)) {
        const normalizedKey = key.replace(/\s/g, '').toLowerCase();

        // 정규화된 키가 일치하거나 포함 관계이면 매칭
        if (normalizedKey === normalizedFieldName ||
            normalizedKey.includes(normalizedFieldName) ||
            normalizedFieldName.includes(normalizedKey)) {
            if (data[key] && String(data[key]).trim() !== '') {
                return true;
            }
        }
    }

    return false;
}

/**
 * 문서 타입 이름으로 페이지 찾기 (부분 매칭)
 */
function findPagesByDocTypeName(pagesByDocType, templateName) {
    for (const [docType, pages] of pagesByDocType.entries()) {
        if (docType.includes(templateName) || templateName.includes(docType)) {
            return pages;
        }
        // 정규화된 비교
        if (docType.toLowerCase().replace(/\s/g, '') === templateName.toLowerCase().replace(/\s/g, '')) {
            return pages;
        }
    }
    return [];
}

/**
 * 데이터에서 매칭 값 추출 (별칭 무시 간단 버전 + 공백 무시)
 */
function extractMatchValueFromData(data, key) {
    if (!data || !key) return null;

    // 🔧 FIX: Normalize by removing spaces
    const normalizedKey = key.replace(/\s/g, '').toLowerCase();

    // 직접 매칭
    if (data[key]) return String(data[key]).trim();

    // 정규화된 매칭 (공백 제거 후 비교)
    for (const dataKey of Object.keys(data)) {
        const normalizedDataKey = dataKey.replace(/\s/g, '').toLowerCase();

        if (normalizedDataKey === normalizedKey ||
            normalizedDataKey.includes(normalizedKey) ||
            normalizedKey.includes(normalizedDataKey)) {
            if (data[dataKey]) return String(data[dataKey]).trim();
        }
    }

    return null;
}

export default {
    mergePageResults,
    applyMergeRules,
    findPagesByDocType,
    getDocTypesInGroup,
    getFirstPageNumber,
    getPageNumbers
};
